<script setup>
// 诚实「不可写目标」落地页(nuxt#26,扩展 #25)。承接 "X to <非可写目标>" 长尾搜索 —— 不做假 converter、
// 不 404 —— 解释为什么做不到/暂未上线,再把意图导向真正可行的转换。内容完全由 seo payload 驱动
// (reasons/alternatives/verdict 等来自 explainer-data.js),组件只负责渲染。
// 由 free-tools/[...slug].vue 在 seo.explainer 时渲染(替代 FreeToolPage)。
const props = defineProps({ seo: { type: Object, required: true } });

const from = computed(() => props.seo.from || "your image");
const to = computed(() => props.seo.to || "RAW");
const reasons = computed(() => props.seo.reasons || []);
const alternatives = computed(() => props.seo.alternatives || []);
const isSoon = computed(() => props.seo.verdictTone === "soon");
</script>

<template>
  <!-- 嵌入 FreeToolPage 的动作区(替代上传/转换控件):只渲染"为什么做不到 + 能做什么",
       面包屑/Hero/格式详情/FAQ/广告/推荐由 FreeToolPage 外壳统一提供,避免重复 + 吃到广告和推荐流量。 -->
  <div class="ft-scope rt-explainer rt-embed">
    <!-- 红色 verdict 居中(Hero 副标题已做提问式引导,这里不再重复 lead) -->
    <p class="rt-verdict" :class="{ 'rt-verdict-soon': isSoon }">
      <span class="rt-x" aria-hidden="true">{{ isSoon ? "!" : "✕" }}</span>
      {{ seo.verdict }}
    </p>

    <!-- Why module -->
    <section class="rt-why" aria-label="Why this isn't possible">
      <h2>{{ seo.whyTitle }}</h2>
      <div class="rt-why-grid">
        <article v-for="(r, i) in reasons" :key="i" class="rt-card">
          <span class="rt-num">{{ i + 1 }}</span>
          <h3>{{ r.h }}</h3>
          <p>{{ r.p }}</p>
        </article>
      </div>
    </section>

    <!-- Target-family landscape (e.g. the actual camera RAW formats) -->
    <section class="rt-formats" v-if="seo.targetFormats && seo.targetFormats.length" aria-label="Format landscape">
      <h2>{{ seo.targetFormatsTitle }}</h2>
      <p class="rt-sub">{{ seo.targetFormatsSub }}</p>
      <div class="rt-fmt-grid">
        <a v-for="f in seo.targetFormats" :key="f.ext" class="rt-fmt" :href="f.href">
          <span class="rt-fmt-ext">{{ f.ext }}</span>
          <span class="rt-fmt-meta">
            <span class="rt-fmt-full">{{ f.full }}</span>
            <span class="rt-fmt-dev">{{ f.dev }}</span>
          </span>
        </a>
      </div>
    </section>

    <!-- What you can do instead -->
    <section class="rt-alt" aria-label="What you can do instead">
      <h2>What you can do instead</h2>
      <div class="rt-alt-grid">
        <a v-for="(a, i) in alternatives" :key="i" class="rt-alt-card" :href="a.to">
          <h3>{{ a.title }}</h3>
          <p>{{ a.desc }}</p>
          <span class="rt-alt-cta">{{ a.cta }} <span aria-hidden="true">→</span></span>
        </a>
      </div>
    </section>
  </div>
</template>

<style scoped>
.rt-explainer {
  /* 顶部不留白:面包屑靠自身 .crumb 的 padding-top:22px 贴顶,与 FreeToolPage 一致(原 56px 会把面包屑整体下压)。 */
  padding: 0 0 88px;
  min-height: 60vh;
}
/* 嵌入工具页动作区:去掉独立页的上下留白 + 全部左对齐、按列宽排布(独立页那套 margin:auto 居中 / 窄 max-width
   在窄的 tool-col 里会让 verdict/lead/标题错位)。 */
.rt-explainer.rt-embed {
  padding: 0;
  min-height: 0;
}
.rt-explainer.rt-embed .rt-verdict { display: flex; width: fit-content; max-width: 640px; margin: 0 auto 32px; }
.rt-explainer.rt-embed .rt-why,
.rt-explainer.rt-embed .rt-alt { max-width: none; margin: 0; }
.rt-explainer.rt-embed .rt-why { margin-bottom: 40px; }
.rt-explainer.rt-embed .rt-alt { margin-top: 44px; }
.rt-explainer.rt-embed .rt-why h2,
.rt-explainer.rt-embed .rt-alt h2 { text-align: center; }
.rt-explainer .rt-crumb {
  margin-bottom: 38px; /* 原顶部留白转成 面包屑→hero 的间距 */
}
.rt-explainer .rt-hero {
  max-width: 760px;
  margin: 0 auto 48px;
  text-align: center;
}
.rt-explainer .rt-hero .eyebrow {
  display: block;
  margin-bottom: 14px;
}
.rt-explainer .rt-hero h1 {
  font-size: clamp(30px, 5vw, 46px);
  color: var(--tx);
  margin-bottom: 16px;
}
.rt-explainer .rt-lead {
  font-size: 18px;
  line-height: 1.6;
  color: var(--tx-dim);
  max-width: 600px;
  margin: 0 auto 22px;
}
.rt-explainer .rt-verdict {
  display: inline-flex;
  align-items: flex-start;
  gap: 10px;
  text-align: left;
  max-width: 580px;
  margin: 0 auto;
  padding: 14px 18px;
  border: 1px solid rgba(248, 113, 113, 0.3);
  border-radius: 12px;
  background: rgba(248, 113, 113, 0.07);
  color: var(--tx);
  font-size: 15px;
  line-height: 1.5;
  font-weight: 500;
}
.rt-explainer .rt-x {
  flex: none;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f87171;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  margin-top: 1px;
}
/* "coming soon" tone — amber, not a hard red "no" */
.rt-explainer .rt-verdict-soon {
  border-color: rgba(251, 191, 36, 0.32);
  background: rgba(251, 191, 36, 0.08);
}
.rt-explainer .rt-verdict-soon .rt-x {
  background: #fbbf24;
  color: #1a1205;
}

/* Why module */
.rt-explainer .rt-why,
.rt-explainer .rt-alt {
  max-width: 980px;
  margin: 0 auto;
}
.rt-explainer .rt-why h2,
.rt-explainer .rt-alt h2 {
  font-size: clamp(22px, 3vw, 28px);
  color: var(--tx);
  text-align: center;
  margin-bottom: 28px;
}
.rt-explainer .rt-why-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 18px;
}
.rt-explainer .rt-card {
  position: relative;
  padding: 26px 24px 24px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--surface);
}
.rt-explainer .rt-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-bottom: 14px;
  border-radius: 9px;
  background: rgba(139, 92, 246, 0.16);
  color: var(--brand-2);
  font-weight: 700;
  font-size: 15px;
}
.rt-explainer .rt-card h3 {
  font-size: 17px;
  color: var(--tx);
  margin-bottom: 10px;
}
.rt-explainer .rt-card p {
  font-size: 14.5px;
  line-height: 1.6;
  color: var(--tx-dim);
}

/* Alternatives */
.rt-explainer .rt-alt {
  margin-top: 64px;
}
.rt-explainer .rt-alt-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}
.rt-explainer .rt-alt-card {
  display: flex;
  flex-direction: column;
  padding: 24px 22px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--surface);
  transition: border-color 0.18s, transform 0.12s, background 0.18s;
}
.rt-explainer .rt-alt-card:hover {
  border-color: rgba(139, 92, 246, 0.5);
  background: rgba(139, 92, 246, 0.06);
  transform: translateY(-2px);
}
.rt-explainer .rt-alt-card h3 {
  font-size: 17px;
  color: var(--tx);
  margin-bottom: 8px;
}
.rt-explainer .rt-alt-card p {
  font-size: 14px;
  line-height: 1.55;
  color: var(--tx-dim);
  margin-bottom: 16px;
  flex: 1;
}
.rt-explainer .rt-alt-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--brand-2);
  font-weight: 600;
  font-size: 14.5px;
}
.rt-explainer .rt-alt-card:hover .rt-alt-cta {
  color: #c4b1ff;
}

/* Common RAW formats */
.rt-explainer .rt-formats {
  max-width: 980px;
  margin: 64px auto 0;
}
.rt-explainer .rt-formats h2,
.rt-explainer .rt-faq h2 {
  font-size: clamp(22px, 3vw, 28px);
  color: var(--tx);
  text-align: center;
  margin-bottom: 12px;
}
.rt-explainer .rt-sub {
  max-width: 620px;
  margin: 0 auto 28px;
  text-align: center;
  font-size: 15px;
  line-height: 1.6;
  color: var(--tx-dim);
}
.rt-explainer .rt-fmt-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(212px, 1fr));
  gap: 10px;
}
.rt-explainer .rt-fmt {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 14px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--surface);
}
.rt-explainer a.rt-fmt {
  transition: border-color 0.16s, background 0.16s, transform 0.12s;
}
.rt-explainer a.rt-fmt:hover {
  border-color: rgba(139, 92, 246, 0.5);
  background: rgba(139, 92, 246, 0.06);
  transform: translateY(-1px);
}
.rt-explainer .rt-fmt-ext {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 28px;
  padding: 0 8px;
  border-radius: 7px;
  background: rgba(139, 92, 246, 0.16);
  color: var(--brand-2);
  font-weight: 700;
  font-size: 12.5px;
}
.rt-explainer .rt-fmt-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.rt-explainer .rt-fmt-full {
  font-size: 13.5px;
  color: var(--tx);
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rt-explainer .rt-fmt-dev {
  font-size: 12px;
  color: var(--tx-faint);
}

/* Format details（源 + 目标）—— 与 converter 页同深度的格式知识块 */
.rt-explainer .rt-fmtinfo {
  max-width: 980px;
  margin: 64px auto 0;
}
.rt-explainer .rt-fmtinfo h2 {
  font-size: clamp(22px, 3vw, 28px);
  color: var(--tx);
  text-align: center;
  margin-bottom: 28px;
}
.rt-explainer .rt-fmt-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 18px;
}
.rt-explainer .rt-fmt-card {
  padding: 26px 24px 22px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--surface);
}
.rt-explainer .rt-fmt-card h3 {
  font-size: 18px;
  color: var(--tx);
  margin-bottom: 12px;
}
.rt-explainer .rt-fmt-what {
  font-size: 14.5px;
  line-height: 1.65;
  color: var(--tx-dim);
  margin-bottom: 18px;
}
.rt-explainer .rt-fmt-facts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
  margin-bottom: 16px;
}
.rt-explainer .rt-fmt-fact {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 0;
  border-top: 1px solid var(--line);
}
.rt-explainer .rt-fmt-fact dt {
  font-size: 11.5px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--tx-faint);
}
.rt-explainer .rt-fmt-fact dd {
  font-size: 13.5px;
  color: var(--tx);
  margin: 0;
}
.rt-explainer .rt-fmt-open {
  font-size: 13.5px;
  line-height: 1.6;
  color: var(--tx-dim);
  padding-top: 14px;
  border-top: 1px solid var(--line);
}
.rt-explainer .rt-fmt-open span {
  color: var(--brand-2);
  font-weight: 600;
}
.rt-explainer .rt-fmt-guide {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  color: var(--brand-2);
  font-weight: 600;
  font-size: 13.5px;
}
.rt-explainer .rt-fmt-guide:hover {
  color: #c4b1ff;
}

/* FAQ */
.rt-explainer .rt-faq {
  max-width: 760px;
  margin: 64px auto 0;
}
.rt-explainer .rt-faq h2 {
  margin-bottom: 24px;
}
.rt-explainer .rt-faq-item {
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--surface);
  margin-bottom: 10px;
  overflow: hidden;
}
.rt-explainer .rt-faq-item[open] {
  border-color: rgba(139, 92, 246, 0.4);
}
.rt-explainer .rt-faq-q {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  cursor: pointer;
  color: var(--tx);
  font-size: 15.5px;
  font-weight: 600;
  line-height: 1.45;
  list-style: none;
}
.rt-explainer .rt-faq-q::-webkit-details-marker {
  display: none;
}
.rt-explainer .rt-faq-mark {
  position: relative;
  flex: none;
  width: 15px;
  height: 15px;
}
.rt-explainer .rt-faq-mark::before,
.rt-explainer .rt-faq-mark::after {
  content: "";
  position: absolute;
  background: var(--brand-2);
  border-radius: 2px;
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.rt-explainer .rt-faq-mark::before {
  top: 6.5px;
  left: 0;
  width: 15px;
  height: 2px;
}
.rt-explainer .rt-faq-mark::after {
  top: 0;
  left: 6.5px;
  width: 2px;
  height: 15px;
}
.rt-explainer .rt-faq-item[open] .rt-faq-mark::after {
  transform: rotate(90deg);
  opacity: 0;
}
.rt-explainer .rt-faq-a {
  padding: 0 18px 18px;
  font-size: 14.5px;
  line-height: 1.65;
  color: var(--tx-dim);
}

@media (max-width: 560px) {
  .rt-explainer {
    padding: 0 0 64px;
  }
}
</style>
